import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { requestPushPermission, loginPushUser, logoutPushUser, syncPushSubscription, getSubscriptionId } from '../services/pushService';

// Use backend URL from env
const API_BASE = (import.meta?.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(
    () => localStorage.getItem('sf_selected_vehicle') || null
  );

  // Axios instance
  const axiosAuth = axios.create({ baseURL: API_BASE });
  axiosAuth.interceptors.request.use((config) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_BASE}/auth/login`, { email, password });
      const jwt = data.access_token;
      localStorage.setItem('sf_token', jwt);
      setToken(jwt); // triggera useEffect che carica user + history
      return { success: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || `HTTP ${e?.response?.status}` || 'Errore sconosciuto';
      return { success: false, error: `[DEBUG] ${detail}` };
    }
  };

  const register = async ({ name, email, password }) => {
    try {
      await axios.post(`${API_BASE}/auth/register`, { name, email, password });
      // Auto-login after register
      return await login(email, password);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || `HTTP ${e?.response?.status}` || 'Errore sconosciuto';
      return { success: false, error: `[DEBUG] ${detail}` };
    }
  };

  const logout = async () => {
    // Rimuove il subscriptionId OneSignal dal backend prima di azzerare il token
    try {
      const subId = await getSubscriptionId();
      if (subId && token) {
        await axiosAuth.delete(`/users/onesignal/${encodeURIComponent(subId)}`).catch(() => {});
      }
    } catch (_) { /* ignore */ }
    await logoutPushUser();
    setUser(null);
    setMessages([]);
    setToken(null);
    localStorage.removeItem('sf_token');
  };

  const refreshMe = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axiosAuth.get('/auth/me');
      setUser(data);
    } catch (e) {
      const detail = e?.response?.status || e?.message || 'sconosciuto';
      localStorage.setItem('sf_debug_error', `refreshMe fallito: ${detail}`);
      logout();
    }
  }, [token]);

  const refreshHistory = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axiosAuth.get('/messages/history');
      setMessages(data);
    } catch (e) {
      console.error('Errore caricamento cronologia', e);
    }
  }, [token]);

  const updateParkingStatus = async (parkingStatus) => {
    try {
      const { data } = await axiosAuth.patch('/status/me', { parkingStatus });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore aggiornamento stato' };
    }
  };

  const updateSettings = async (settings) => {
    try {
      const { data } = await axiosAuth.patch('/users/me/settings', settings);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore aggiornamento impostazioni' };
    }
  };

  const updateProfile = async ({ name, email }) => {
    try {
      const { data } = await axiosAuth.put('/users/me', { name, email });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore aggiornamento profilo' };
    }
  };

  const respondToMessage = async () => ({ success: false });

  const markAsRead = async () => ({ success: false });

  // === Auto condivise ===
  const selectVehicle = useCallback((id) => {
    setSelectedVehicleId(id);
    if (id) localStorage.setItem('sf_selected_vehicle', id);
  }, []);

  const refreshVehicles = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axiosAuth.get('/vehicles');
      setVehicles(data);
      // garantisce che l'auto selezionata sia valida; altrimenti usa la predefinita
      setSelectedVehicleId((cur) => {
        if (cur && data.some((v) => v.id === cur)) return cur;
        const def = data.find((v) => v.isDefault) || data[0];
        const next = def ? def.id : null;
        if (next) localStorage.setItem('sf_selected_vehicle', next);
        return next;
      });
    } catch (e) {
      console.error('Errore caricamento auto', e);
    }
  }, [token]);

  const createVehicle = async (nickname) => {
    try {
      const { data } = await axiosAuth.post('/vehicles', { nickname });
      await refreshVehicles();
      selectVehicle(data.id);
      return { success: true, vehicle: data };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore creazione auto' };
    }
  };

  const joinVehicleByCode = async (code, nickname) => {
    try {
      const { data } = await axiosAuth.post('/vehicles/join-by-code', { code, nickname });
      await refreshVehicles();
      return { success: true, vehicle: data };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Codice non valido' };
    }
  };

  const setVehicleStatus = async (id, parkingStatus) => {
    try {
      await axiosAuth.patch(`/vehicles/${id}/status`, { parkingStatus });
      await refreshVehicles();
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore aggiornamento stato' };
    }
  };

  const renameVehicle = async (id, nickname) => {
    try {
      await axiosAuth.patch(`/vehicles/${id}/nickname`, { nickname });
      await refreshVehicles();
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore rinomina' };
    }
  };

  const deleteVehicle = async (id) => {
    try {
      await axiosAuth.delete(`/vehicles/${id}`);
      await refreshVehicles();
      return { success: true };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore eliminazione' };
    }
  };

  const getVehicleMembers = async (id) => {
    try {
      const { data } = await axiosAuth.get(`/vehicles/${id}/members`);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: e?.response?.data?.detail || 'Errore membri' };
    }
  };

  // Polling recent messages every 30s
  useEffect(() => {
    const savedToken = localStorage.getItem('sf_token');
    if (savedToken) {
      setToken(savedToken);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      await refreshMe();
      await refreshHistory();
      await refreshVehicles();
      setLoading(false);
    })();
  }, [token, refreshMe, refreshHistory, refreshVehicles]);

  // Aggiornamento periodico dei messaggi (così nuove scansioni compaiono e
  // i messaggi a cui si è risposto spariscono, senza riaprire l'app)
  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => { refreshHistory(); }, 15000);
    return () => clearInterval(id);
  }, [token, refreshHistory]);

  // Associa device OneSignal all'utente autenticato (no-op su web)
  useEffect(() => {
    if (user?.id) {
      (async () => {
        await requestPushPermission();
        await loginPushUser(user.id);
        // sync subscriptionId al backend (multi-device, retry interno)
        await syncPushSubscription(axiosAuth);
      })();
    }
  }, [user?.id]);

  const value = {
    isAuthenticated: !!user,
    user,
    messages,
    loading,
    login,
    register,
    logout,
    updateParkingStatus,
    updateSettings,
    updateProfile,
    respondToMessage,
    markAsRead,
    refreshHistory,
    vehicles,
    selectedVehicleId,
    selectVehicle,
    refreshVehicles,
    createVehicle,
    joinVehicleByCode,
    setVehicleStatus,
    renameVehicle,
    deleteVehicle,
    getVehicleMembers,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
